import React, { useState, useEffect } from 'react';
import { getEvidenceRecords, verifyEvidence, getLegalEntities, getOperatingEntities } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatDateTime, truncateText } from '../lib/utils';
import { Search, FileText, Shield, ShieldCheck, ShieldX } from 'lucide-react';
import { toast } from 'sonner';

const EvidencePage = () => {
  const [records, setRecords] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [operatingEntities, setOperatingEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    channel: '',
    legal_entity_id: '',
    operating_entity_id: ''
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [verification, setVerification] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (search) params.search = search;
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      
      const [recordsRes, legalRes, operatingRes] = await Promise.all([
        getEvidenceRecords(params),
        getLegalEntities(),
        getOperatingEntities()
      ]);
      setRecords(recordsRes.data);
      setLegalEntities(legalRes.data);
      setOperatingEntities(operatingRes.data);
    } catch (error) {
      toast.error('Failed to load evidence records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleVerify = async (record) => {
    try {
      const response = await verifyEvidence(record.id);
      setVerification(response.data);
      setSelectedRecord(record);
    } catch (error) {
      toast.error('Failed to verify');
    }
  };

  const getEntityName = (id, entities) => entities.find(e => e.id === id)?.name || '-';

  return (
    <div className="space-y-6" data-testid="evidence-page">
      {/* Header */}
      <div>
        <h1 className="font-manrope text-xl font-bold text-zinc-900">Evidence Archive</h1>
        <p className="text-sm text-zinc-500 font-inter">Audit-ready transcript storage with integrity verification</p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[300px]">
          <Input
            placeholder="Search transcripts, participants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm"
            data-testid="evidence-search-input"
          />
          <Button type="submit" variant="outline" className="rounded-sm">
            <Search className="w-4 h-4" />
          </Button>
        </form>
        
        <Select value={filters.channel || 'all'} onValueChange={(v) => setFilters({ ...filters, channel: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-32 rounded-sm">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Meet">Meet</SelectItem>
            <SelectItem value="Zoom">Zoom</SelectItem>
            <SelectItem value="Phone">Phone</SelectItem>
            <SelectItem value="WhatsApp">WhatsApp</SelectItem>
            <SelectItem value="In-person">In-person</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.legal_entity_id || 'all'} onValueChange={(v) => setFilters({ ...filters, legal_entity_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40 rounded-sm">
            <SelectValue placeholder="Legal Entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Legal</SelectItem>
            {legalEntities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Records */}
      {loading ? (
        <div className="text-center py-8 text-zinc-500 font-inter">Loading...</div>
      ) : records.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-8 text-center">
            <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-zinc-500 font-inter">No evidence records found. Use Intake to add transcripts.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {records.map((record) => (
            <Card key={record.id} className="border border-zinc-200 shadow-none rounded-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-inter text-sm font-medium flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{record.channel}</Badge>
                    {formatDateTime(record.captured_at_timestamp)}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge className="text-xs bg-zinc-100 text-zinc-800">{record.confidentiality}</Badge>
                    {record.legal_hold && <Badge className="bg-red-500 text-white text-xs">Legal Hold</Badge>}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 text-xs rounded-sm"
                      onClick={() => handleVerify(record)}
                      data-testid={`verify-${record.id}`}
                    >
                      <Shield className="w-3 h-3 mr-1" /> Verify
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-zinc-500 font-inter mb-2">
                  Participants: {record.participants?.join(', ')} | 
                  Op: {getEntityName(record.operating_entity_id, operatingEntities)} | 
                  Legal: {getEntityName(record.legal_entity_id, legalEntities)}
                </p>
                <pre className="text-sm font-mono bg-zinc-50 p-3 rounded-sm whitespace-pre-wrap max-h-32 overflow-auto">
                  {truncateText(record.transcript_text, 500)}
                </pre>
                <p className="text-xs text-zinc-400 font-mono mt-2">
                  SHA-256: {record.transcript_hash}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Verification Dialog */}
      <Dialog open={!!selectedRecord} onOpenChange={() => { setSelectedRecord(null); setVerification(null); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              {verification?.integrity_valid ? (
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
              ) : (
                <ShieldX className="w-5 h-5 text-red-500" />
              )}
              Integrity Verification
            </DialogTitle>
          </DialogHeader>
          {verification && (
            <div className="space-y-4">
              <div className={`p-4 rounded-sm ${verification.integrity_valid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`font-medium ${verification.integrity_valid ? 'text-emerald-800' : 'text-red-800'}`}>
                  {verification.integrity_valid ? 'INTEGRITY VALID' : 'INTEGRITY COMPROMISED'}
                </p>
                <p className={`text-sm ${verification.integrity_valid ? 'text-emerald-600' : 'text-red-600'}`}>
                  {verification.integrity_valid 
                    ? 'The transcript has not been modified since capture.'
                    : 'WARNING: The transcript may have been altered.'}
                </p>
              </div>
              <div className="space-y-2 text-sm font-mono">
                <div>
                  <p className="text-zinc-500">Stored Hash:</p>
                  <p className="break-all">{verification.stored_hash}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Computed Hash:</p>
                  <p className="break-all">{verification.computed_hash}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidencePage;
